import React, { useMemo } from 'react';
import {
  Bar, BarChart, CartesianGrid, Cell, ComposedChart, Legend, Line, Pie, PieChart,
  ResponsiveContainer, Tooltip, XAxis, YAxis,
} from 'recharts';
import { inr, num } from './format';

const COLORS = ['#4f46e5', '#10b981', '#f59e0b', '#ef4444', '#0ea5e9', '#64748b', '#a855f7', '#14b8a6'];
const short = (v) => (Math.abs(v) >= 100000 ? `${(v / 100000).toFixed(1)}L` : Math.abs(v) >= 1000 ? `${(v / 1000).toFixed(1)}k` : v);
const monthLabel = (m) => {
  const [y, mo] = m.split('-');
  return new Date(Number(y), Number(mo) - 1, 1).toLocaleString('en-IN', { month: 'short', year: '2-digit' });
};

const Panel = ({ title, subtitle, children, testId, className = '' }) => (
  <div className={`rounded-xl border border-slate-200 bg-white p-4 ${className}`} data-testid={testId}>
    <div className="text-sm font-semibold text-slate-800">{title}</div>
    {subtitle && <div className="text-[11px] text-slate-500 mb-2">{subtitle}</div>}
    <div className="h-64">{children}</div>
  </div>
);

const FulfillmentChart = ({ monthly, onDrill }) => (
  <Panel title="PO vs GRN Fulfillment Quantity" subtitle="Ordered (PO) vs received (GRN) quantity by PO month, with fill rate % · click a bar to drill into that month" testId="chart-fulfillment" className="lg:col-span-2">
    <ResponsiveContainer>
      <ComposedChart data={monthly.map((m) => ({ ...m, label: monthLabel(m.month), fill_rate: m.ordered_qty ? Math.min(100, Math.round(m.received_qty / m.ordered_qty * 1000) / 10) : null }))}>
        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
        <XAxis dataKey="label" fontSize={11} />
        <YAxis yAxisId="q" fontSize={11} tickFormatter={short} />
        <YAxis yAxisId="p" orientation="right" fontSize={11} domain={[0, 100]} unit="%" />
        <Tooltip formatter={(v, n) => (n === 'Fill Rate' ? `${v}%` : num(v))} />
        <Legend wrapperStyle={{ fontSize: 11 }} />
        <Bar yAxisId="q" dataKey="ordered_qty" name="PO Qty" fill="#cbd5e1" radius={[3, 3, 0, 0]} cursor="pointer" onClick={(d) => onDrill('month', d?.payload?.month)} />
        <Bar yAxisId="q" dataKey="received_qty" name="GRN Qty" fill="#4f46e5" radius={[3, 3, 0, 0]} cursor="pointer" onClick={(d) => onDrill('month', d?.payload?.month)} />
        <Line yAxisId="p" dataKey="fill_rate" name="Fill Rate" stroke="#f59e0b" strokeWidth={2} dot={{ r: 3 }} />
      </ComposedChart>
    </ResponsiveContainer>
  </Panel>
);

const ReconChart = ({ recon, onDrill }) => (
  <Panel title="PO Lines by Reconciliation Status" subtitle="Share of PO lines in each receipt state · click a slice to filter" testId="chart-recon">
    <ResponsiveContainer>
      <PieChart>
        <Pie data={recon} dataKey="value" nameKey="name" innerRadius={50} outerRadius={85} paddingAngle={2} cursor="pointer" onClick={(d) => onDrill('recon', d?.name || d?.payload?.name)}>
          {recon.map((d, i) => <Cell key={d.name} fill={COLORS[i % COLORS.length]} />)}
        </Pie>
        <Tooltip />
        <Legend wrapperStyle={{ fontSize: 11 }} />
      </PieChart>
    </ResponsiveContainer>
  </Panel>
);

const SupplierChart = ({ suppliers, onDrill }) => {
  const data = useMemo(() => {
    const total = suppliers.reduce((a, s) => a + (s.po_value || 0), 0);
    const top = suppliers.slice(0, 8).map((s) => ({
      name: s.supplier_name, po_value: s.po_value, grn_value: s.grn_received_value,
      share: total ? Math.round(s.po_value / total * 1000) / 10 : 0,
    }));
    const rest = suppliers.slice(8);
    if (rest.length) {
      const pv = rest.reduce((a, s) => a + s.po_value, 0);
      top.push({ name: `Others (${rest.length})`, po_value: pv, grn_value: rest.reduce((a, s) => a + s.grn_received_value, 0), share: total ? Math.round(pv / total * 1000) / 10 : 0 });
    }
    return top;
  }, [suppliers]);
  return (
    <Panel title="Supplier Contribution" subtitle="PO value vs GRN value by supplier (share of total PO value) · click a bar to filter" testId="chart-supplier-contribution" className="lg:col-span-2">
      <ResponsiveContainer>
        <BarChart data={data} layout="vertical" margin={{ left: 10, right: 20 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" horizontal={false} />
          <XAxis type="number" fontSize={11} tickFormatter={short} />
          <YAxis type="category" dataKey="name" width={150} fontSize={11} tickFormatter={(n) => (n.length > 22 ? `${n.slice(0, 21)}…` : n)} />
          <Tooltip formatter={(v, n, p) => (n === 'PO Value' ? `${inr(v)} (${p.payload.share}%)` : inr(v))} />
          <Legend wrapperStyle={{ fontSize: 11 }} />
          <Bar dataKey="po_value" name="PO Value" fill="#4f46e5" radius={[0, 3, 3, 0]} cursor="pointer" onClick={(d) => onDrill('supplier', d?.payload?.name)} />
          <Bar dataKey="grn_value" name="GRN Value" fill="#10b981" radius={[0, 3, 3, 0]} cursor="pointer" onClick={(d) => onDrill('supplier', d?.payload?.name)} />
        </BarChart>
      </ResponsiveContainer>
    </Panel>
  );
};

const ValueTrendChart = ({ monthly }) => (
  <Panel title="PO Value vs GRN Value Trend" subtitle="Monthly ordered value vs received value (incl. GST)" testId="chart-value-trend">
    <ResponsiveContainer>
      <ComposedChart data={monthly.map((m) => ({ ...m, label: monthLabel(m.month) }))}>
        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
        <XAxis dataKey="label" fontSize={11} />
        <YAxis fontSize={11} tickFormatter={short} />
        <Tooltip formatter={(v) => inr(v)} />
        <Legend wrapperStyle={{ fontSize: 11 }} />
        <Line dataKey="po_value" name="PO Value" stroke="#4f46e5" strokeWidth={2} dot={{ r: 3 }} />
        <Line dataKey="grn_value" name="GRN Value" stroke="#10b981" strokeWidth={2} dot={{ r: 3 }} />
      </ComposedChart>
    </ResponsiveContainer>
  </Panel>
);

export const OverviewCharts = ({ breakdowns, suppliers = [], onDrill }) => {
  const b = breakdowns || { recon: [], monthly: [] };
  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
      <FulfillmentChart monthly={b.monthly || []} onDrill={onDrill} />
      <ReconChart recon={b.recon || []} onDrill={onDrill} />
      <SupplierChart suppliers={suppliers} onDrill={onDrill} />
      <ValueTrendChart monthly={b.monthly || []} />
    </div>
  );
};
